import { PREVIEW_FRAME_ROOT, PREVIEW_MODE } from '$lib/global/constants';

import debug from 'debug';

import { prefersReducedMotion } from './accessibility';

const debugLog = debug('app:scroll-to-component');

interface ScrollConfig {
    dataAttribute: string;
    scrollBehavior: ScrollBehavior;
    waitTimeout: number;
    postNavigationDelay: number;
}

const DEFAULT_CONFIG: ScrollConfig = {
    dataAttribute: 'data-scroll-id',
    scrollBehavior: 'smooth',
    waitTimeout: 5000,
    postNavigationDelay: 150,
};

interface ScrollTargetGeometry {
    /** The element's viewport-relative top (element.getBoundingClientRect().top). */
    elementTop: number;
    /** The scroll container's current scrollTop (window.scrollY for the window). */
    currentScrollTop: number;
    /** The container's viewport-relative top (getBoundingClientRect().top; 0 for the window). */
    containerTop: number;
    /** Height of a fixed header to offset the target by. */
    headerHeight: number;
}

/**
 * Resolves the scrollTop that brings an element to the top of its scroll
 * container's viewport.
 *
 * getBoundingClientRect() is viewport-relative, so it must be translated into
 * the container's own scroll coordinate space. For the window this collapses to
 * `window.scrollY + elementTop - headerHeight` (containerTop is 0). For a nested
 * scroll container (e.g. the preview frame) the window never scrolls, so the
 * container's scrollTop and viewport offset must be used instead — otherwise a
 * target above the current viewport produces a negative offset that clamps to 0
 * and jumps to the top of the page (FUN-3942).
 */
export const computeScrollTarget = ({
    elementTop,
    currentScrollTop,
    containerTop,
    headerHeight,
}: ScrollTargetGeometry): number => currentScrollTop + elementTop - containerTop - headerHeight;

/**
 * Creates a scroll navigation module with configured behavior
 * @param config - Optional configuration to override defaults
 * @returns Scroll navigation functions
 */
const createScrollNavigation = (config: Partial<ScrollConfig> = {}) => {
    const settings = { ...DEFAULT_CONFIG, ...config };

    const componentExists = (componentId: string): boolean => {
        const element = document.querySelector(`[${settings.dataAttribute}="${componentId}"]`);
        return !!element;
    };

    const getHeaderHeight = (): number => {
        const header = document.querySelector('[aria-label="Site header"][data-is-fixed="true"]');

        if (header) {
            return header.getBoundingClientRect().height;
        }
        return 0;
    };

    const scrollToElement = (componentId: string): void => {
        const element = document.querySelector(`[${settings.dataAttribute}="${componentId}"]`);

        if (!element) {
            return;
        }

        // Respect user's motion preferences
        const behavior = prefersReducedMotion() ? 'auto' : settings.scrollBehavior;

        const elementTop = element.getBoundingClientRect().top;
        const headerHeight = getHeaderHeight();

        if (PREVIEW_MODE) {
            const previewFrameRoot = document.getElementById(PREVIEW_FRAME_ROOT);

            if (previewFrameRoot) {
                // In preview mode the funnel is rendered inside a scrollable
                // container rather than the window, so resolve the target
                // against the container's own scroll coordinate space.
                const y = computeScrollTarget({
                    elementTop,
                    currentScrollTop: previewFrameRoot.scrollTop,
                    containerTop: previewFrameRoot.getBoundingClientRect().top,
                    headerHeight,
                });

                previewFrameRoot.scrollTo({
                    top: y,
                    behavior,
                });
            }
        } else {
            const y = computeScrollTarget({
                elementTop,
                currentScrollTop: window.scrollY,
                containerTop: 0,
                headerHeight,
            });

            window.scrollTo({ top: y, behavior });
        }

        if (element instanceof HTMLElement) {
            element.focus({ preventScroll: true });

            // Element not focusable, set tabindex to 0 and focus it
            if (document.activeElement !== element) {
                element.setAttribute('tabindex', '0');
                element.focus({ preventScroll: true });
            }
        }
    };

    const waitForComponent = (componentId: string): Promise<void> => {
        return new Promise((resolve, reject) => {
            if (componentExists(componentId)) {
                resolve();
                return;
            }

            const observer = new MutationObserver(() => {
                if (componentExists(componentId)) {
                    observer.disconnect();
                    clearTimeout(timeout);
                    resolve();
                }
            });

            observer.observe(document.body, {
                childList: true,
                subtree: true,
            });

            const timeout = setTimeout(() => {
                observer.disconnect();
                reject(new Error(`Timeout waiting for component "${componentId}"`));
            }, settings.waitTimeout);
        });
    };

    const scrollOnSamePage = (componentId: string): void => {
        if (!componentId) {
            return;
        }

        scrollToElement(componentId);
    };

    const scrollAfterNavigation = async (componentId: string): Promise<void> => {
        if (!componentId) {
            return;
        }

        try {
            await waitForComponent(componentId);

            // Small delay to freeze the page and then scroll to the component
            setTimeout(() => {
                scrollToElement(componentId);
            }, settings.postNavigationDelay);
        } catch (error) {
            debugLog('Error waiting for component:', error);
        }
    };

    return {
        scrollOnSamePage,
        scrollAfterNavigation,
    };
};

export const scrollNavigation = createScrollNavigation();

export { createScrollNavigation };
